// Generic node card for plugin-contributed node types. Reads its pin schema
// and config fields from the plugin manifest passed in via `data._manifest`
// (set when the node is constructed in the editor). Renders one Handle per
// declared pin and one <input> per declared dataIn pin that has a default.
//
// This removes the per-plugin-node React-component requirement — the
// manifest is enough.

import { useEffect, useState } from 'react'
import { Handle, Position } from '@xyflow/react'
import { ogsFileHref } from '../../lib/ogsAssetLink'
import { resolveCharacterId } from '../../lib/characterState'
import { getStatus, subscribe } from '../../lib/ogsAssetStatus'
import { precisionLabel } from '../../lib/ogsPrecision'
import { selectedBorder } from './selectedBorder.js'
import { renderControl, ToggleRow, ConfigRows, useCollapsedSections } from './SettingsSections.jsx'

export function PluginNode({ id, data, selected }) {
  // Collapsed by default; view state only, not saved with the blueprint.
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [collapsed, toggleSection] = useCollapsedSections()
  const manifest = data._manifest
  if (!manifest) {
    return (
      <div style={{
        background: '#2a1010', border: '2px solid #802020',
        borderRadius: 6, padding: 10, color: '#f88',
        fontFamily: 'var(--m-font)', fontSize: 11,
      }}>
        Plugin node missing manifest
      </div>
    )
  }

  const pins = manifest.pins ?? {}
  const execIn  = pins.execIn  ?? []
  const execOut = pins.execOut ?? []
  const dataIn  = pins.dataIn  ?? []
  const dataOut = pins.dataOut ?? []
  const config  = pins.config  ?? []
  const advancedConfig = config.filter(pin => pin.advanced)

  return (
    <div style={{
      background: selected ? '#1a1d2a' : '#10131e',
      border: `2px solid ${selected ? selectedBorder('#503070') : '#503070'}`,
      borderRadius: 6,
      padding: '10px 14px',
      minWidth: 220,
      color: '#ddd',
      fontFamily: 'var(--m-font)',
      fontSize: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <span style={{ fontSize: 12 }}>🔌</span>
        {/* The plugin type id lives in the title's tooltip, not on the card. */}
        <span title={manifest.description ? `${manifest.description}\n\n${manifest.type}` : manifest.type}
          style={{ color: '#c8a0e8', fontWeight: 'bold', fontSize: 11 }}>
          {(manifest.label ?? manifest.type).toUpperCase()}
        </span>
      </div>

      {/* Exec pins (top row) */}
      {(execIn.length > 0 || execOut.length > 0) && (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, position: 'relative', minHeight: 16 }}>
          <div>
            {execIn.map(p => (
              <span key={p.id} style={{ color: '#a0a0a0', fontSize: 10, marginRight: 8, position: 'relative' }}>
                <Handle type="target" position={Position.Left} id={p.id}
                  style={{ background: '#ffffff', width: 10, height: 10 }} />
                {' '}{p.id}
              </span>
            ))}
          </div>
          <div>
            {execOut.map(p => (
              <span key={p.id} style={{ color: '#a0a0a0', fontSize: 10, marginLeft: 8, position: 'relative' }}>
                {p.id}{' '}
                <Handle type="source" position={Position.Right} id={p.id}
                  style={{ background: '#ffffff', width: 10, height: 10 }} />
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Data input pins. `anim`-typed pins are connection-only (the larger
          orange clip-weight handle, matching the output convention); other
          types render an inline editor next to a smaller green data handle. */}
      {dataIn.map(pin => {
        const isAnim = pin.type === 'anim' || pin.id === 'anim'
        const value = data[pin.id] ?? pin.default
        return (
          <div key={pin.id} style={{ display: 'flex', alignItems: 'center', marginBottom: 4, position: 'relative' }}>
            <Handle
              type="target" position={Position.Left} id={pin.id}
              style={isAnim
                ? { background: '#e07040', width: 10, height: 10 }
                : { background: '#4ecf7e', width: 7, height: 7, top: '50%', transform: 'translateY(-50%)' }}
            />
            <span
              title={pin.description}
              style={{ color: '#708898', fontSize: 10, flex: 1, marginLeft: 12, cursor: pin.description ? 'help' : undefined }}
            >
              {pin.label ?? pin.id}
            </span>
            {!isAnim && renderControl(pin, value, data.onChange)}
          </div>
        )
      })}

      {/* Data output pins. `anim`-typed pins render as the larger orange
          clip-weight handle (same convention as LiveLink / PlayClip); any
          other output type renders as a smaller yellow data handle. */}
      {dataOut.map(pin => {
        const isAnim = pin.type === 'anim' || pin.id === 'anim'
        return (
          <div key={pin.id} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 4, position: 'relative' }}>
            {!isAnim && pin.label && (
              <span style={{ color: '#708898', fontSize: 10, marginRight: 12 }}>{pin.label}</span>
            )}
            <Handle
              type="source" position={Position.Right} id={pin.id}
              style={isAnim
                ? { background: '#e07040', width: 10, height: 10 }
                : { background: '#e0a040', width: 7, height: 7, top: '50%', transform: 'translateY(-50%)' }}
            />
          </div>
        )
      })}

      {/* Config: settings (not graph inputs) — a labeled control per pin, no handle.
          A pin's `section` groups it under a small title (shown where the
          section changes, in manifest order). Pins marked `advanced: true` sit
          in a collapsed "Advanced" section; a node can keep that section shown
          while it is empty with `advancedSection: true`. */}
      {config.length > 0 && (
        <div style={{ marginTop: 6, borderTop: '1px solid #22323e', paddingTop: 6 }}>
          <ConfigRows pins={config.filter(pin => !pin.advanced)} data={data}
            collapsed={collapsed} toggle={toggleSection} />
          {(advancedConfig.length > 0 || manifest.advancedSection) && (
            <>
              <ToggleRow open={showAdvanced} onToggle={() => setShowAdvanced(v => !v)}
                label="Advanced" count={advancedConfig.length}
                title={showAdvanced ? 'Hide the advanced settings' : 'Show the advanced settings'} />
              {showAdvanced && (advancedConfig.length > 0
                ? <ConfigRows pins={advancedConfig} data={data} collapsed={collapsed} toggle={toggleSection} firstRow={false} />
                : <div style={{ color: '#4a5a68', fontSize: 9, padding: '2px 0 4px' }}>Nothing here yet.</div>)}
            </>
          )}
          {manifest.type === 'plugin.ogs.avatar' && (
            <OgsPrecisionReadout chosen={data.precision ?? 'fp16'} />
          )}
        </div>
      )}

      {/* Mapping note: which section of the reference inference algorithm this
          node is. Suppressed on the terminal OGSRenderer — it just presents. */}
      {data.note && manifest.type !== 'plugin.ogs.renderer' && (
        <div style={{ color: '#6a7a90', fontSize: 9, marginTop: 6, whiteSpace: 'pre-line', maxWidth: 240 }}>
          {data.note}
        </div>
      )}

      {/* Links to the character /ogs bundle files this node runs (AAM-served).
          The OGSRenderer runs no files of its own (injected lifter/updater),
          so it lists none. */}
      {Array.isArray(data.assets) && data.assets.length > 0 && manifest.type !== 'plugin.ogs.renderer' && (
        <div style={{ marginTop: 6, borderTop: '1px solid #22323e', paddingTop: 6 }}>
          {data.assets.map(f => (
            <div key={f}>
              <a
                href={ogsFileHref(resolveCharacterId(), f) ?? '#'}
                target="_blank" rel="noreferrer"
                onMouseDown={e => e.stopPropagation()}
                style={{ color: '#7ab8e8', fontSize: 10, textDecoration: 'none' }}
              >
                📎 {f}
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// Live "active precision" line for the OGS Avatar node: reflects what the
// decoders actually built at (fp16 auto-downgrades to fp32 when unsupported).
// Picking a new precision rebuilds the decoders in place (no reload) — while the
// swap is mid-flight the pick differs from the applied `requested`, so we show
// "applying…" until the rebuilt decoders report the new precision.
function OgsPrecisionReadout({ chosen }) {
  const [precision, setPrecision] = useState(getStatus().precision)
  useEffect(() => subscribe(() => setPrecision(getStatus().precision)), [])
  if (!precision) return null
  const { requested, geom, appr } = precision
  const active = geom === appr ? precisionLabel(geom) : `geom ${geom} · appr ${appr}`
  return (
    <div style={{ color: '#6a7a90', fontSize: 9, marginTop: 2 }}>
      active: <span style={{ color: '#9ad0f0' }}>{active}</span>
      {chosen !== requested && <span style={{ color: '#e0b45c' }}> · applying…</span>}
    </div>
  )
}
